import { expVal } from '@atlassian/jira-feature-experiments';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';

/**
 * Project-based dynamic config that scopes the Work Item Modernization (WIV) rollout to
 * specific tenants/projects independently of (and in addition to) the global experiment.
 *
 * The experiment key `work_item_modernization_project_based_config` resolves to a `value`
 * parameter shaped as `string[]`. Each entry has the form `cloudId:PATTERN[,PATTERN…]`,
 * where each `PATTERN` is:
 *   - bare (e.g. `ABC`, `A*`)               — include pattern, opt-in
 *   - `!` prefixed (e.g. `!ABC`, `!ARCHIV*`) — exclude pattern, opt-out
 *
 * Patterns support glob wildcards `*` (any chars) and `?` (single char). Multiple entries for the
 * same `cloudId` are merged (all includes ∪, all excludes ∪).
 *
 * @example
 *   [
 *     '11111111-1111-1111-1111-111111111111:A*,DEV,!ABCDEF,!ARCHIV*',
 *     '22222222-...:FOO,BAR',
 *   ]
 */

const REGEX_SPECIAL_CHARS = /[.+^${}()|[\]\\]/g;

/**
 * Compile a glob pattern (`*`, `?`) into an anchored RegExp. Escapes all other regex
 * meta-characters so a runtime config value can never inject regex syntax.
 */
function compileGlobToRegExp(pattern: string): RegExp {
	const escaped = pattern.replace(REGEX_SPECIAL_CHARS, '\\$&');
	const regexBody = escaped.replace(/\*/g, '.*').replace(/\?/g, '.');
	return new RegExp(`^${regexBody}$`);
}

function matchesAnyPattern(patterns: readonly string[], projectKey: string): boolean {
	for (const pattern of patterns) {
		try {
			if (compileGlobToRegExp(pattern).test(projectKey)) {
				return true;
			}
		} catch {
			log.safeErrorWithoutCustomerData(
				'issue.eligibility.is-work-item-modernization-allowed-for-project',
				'Unexpectedly failed to compile glob pattern from WIV project-based dynamic config',
			);
		}
	}
	return false;
}

type ParsedEntry = {
	cloudId: string;
	includes: string[];
	excludes: string[];
};

function parseEntry(entry: string): ParsedEntry | undefined {
	const [rawCloudId, rawValue] = entry.split(':');
	if (rawValue === undefined) {
		return undefined;
	}
	const cloudId = rawCloudId.trim();
	if (!cloudId) {
		return undefined;
	}

	const includes: string[] = [];
	const excludes: string[] = [];

	const rawPatterns = rawValue.split(',');
	for (const rawPattern of rawPatterns) {
		const trimmed = rawPattern.trim();
		if (trimmed.startsWith('!')) {
			const pattern = trimmed.slice(1).trim();
			if (pattern) {
				excludes.push(pattern);
			}
		} else if (trimmed) {
			includes.push(trimmed);
		}
	}

	return { cloudId, includes, excludes };
}

function getConfigEntries(rawConfig: unknown): string[] {
	if (!Array.isArray(rawConfig)) {
		return [];
	}
	return rawConfig.filter((entry): entry is string => typeof entry === 'string');
}

/**
 * Returns true when Work Item Modernization is enabled for the given `(cloudId, issueKey)`.
 *
 * Combines the global experiment with the per-tenant project-based dynamic config kill switch:
 *   - experiment off                     → false (global off)
 *   - cloudId not configured anywhere    → true (config does not apply)
 *   - cloudId configured + exclude match → false (kill switch)
 *   - cloudId configured + include match → true
 *   - cloudId configured, no match       → false (allowlist semantics)
 *   - cloudId or issueKey undefined      → true (cannot resolve, defer to global experiment)
 *
 * `issueKey` is split on `-` to derive the project key, so callers may pass either an issue key
 * (`ABC-123`) or a bare project key (`ABC`). Numeric project IDs do not match any pattern.
 *
 * Safe to call from non-React code (route handlers, SSR data fetchers).
 */
export function isWorkItemModernizationAllowedForProject(
	cloudId: string | undefined,
	issueKey: string | undefined,
): boolean {
	// eslint-disable-next-line jira/ff/no-work-item-modernization-exp-val
	if (!expVal('work_item_modernization', 'isEnabled', false)) {
		return false;
	}

	const projectKey = issueKey?.split('-')[0];
	if (!cloudId || !projectKey) {
		return true;
	}

	const entries = getConfigEntries(
		// eslint-disable-next-line jira/ff/inline-expVal-params -- ESLint rule doesn't understand the array type
		expVal<string[]>('work_item_modernization_project_based_config', 'value', []),
	);

	let cloudIdConfigured = false;
	const includes: string[] = [];
	const excludes: string[] = [];

	for (const rawEntry of entries) {
		const parsed = parseEntry(rawEntry);
		if (parsed && parsed.cloudId === cloudId) {
			cloudIdConfigured = true;
			includes.push(...parsed.includes);
			excludes.push(...parsed.excludes);
		}
	}

	if (!cloudIdConfigured) {
		return true;
	}

	if (matchesAnyPattern(excludes, projectKey)) {
		return false;
	}

	return matchesAnyPattern(includes, projectKey);
}
